import { differenceInCalendarDays } from 'date-fns';
import { useEffect, useRef, useState } from 'react';

import {
  getUserTraits,
  TRELLO_GA_MODAL_DATE,
  TRELLO_M3_OPT_IN_DATE,
  useIsOptimizedUserTraitsEnabled,
  useUserTrait,
} from '@trello/atlassian-personalization';
import { useAaId } from '@trello/authentication';
import type { TouchpointSourceType } from '@trello/cross-flow';
import { useFeatureGate } from '@trello/feature-gate-client';

const DELAY_IN_DAYS = 21;

export const useIsNotProductivityUserOrIsEligible = (
  source: TouchpointSourceType,
) => {
  const [isEligible, setIsEligible] = useState(false);
  const isLoadingTrait = useRef(false);

  const isOptimizedUserTraitsEnabled = useIsOptimizedUserTraitsEnabled();
  const { value: optInTraitValue, loading: loadingOptInTrait } = useUserTrait(
    TRELLO_M3_OPT_IN_DATE,
  );
  const { value: gaModalTraitValue, loading: loadingGaModalTrait } =
    useUserTrait(TRELLO_GA_MODAL_DATE);

  const aaId = useAaId();

  const { value: isInPersonalProductivity, loading: loadingProductivityGate } =
    useFeatureGate('trello_personal_productivity_release');

  const { value: isCheckGaModalTrait } = useFeatureGate(
    'ghost_xf_use_ga_modal_trait',
  );

  useEffect(() => {
    if (isOptimizedUserTraitsEnabled) return;

    const getTrait = async () => {
      isLoadingTrait.current = true;
      try {
        if (!aaId) {
          setIsEligible(false);
          return;
        }
        const traits = await getUserTraits(aaId);
        const optInTrait = traits?.[TRELLO_M3_OPT_IN_DATE];
        const gaModalTrait = !isCheckGaModalTrait
          ? undefined
          : traits?.[TRELLO_GA_MODAL_DATE];

        if (!optInTrait && !gaModalTrait) {
          setIsEligible(false);
          return;
        }
        const traitOptInDate = new Date(optInTrait as string);
        const gaModalTraitDate = gaModalTrait
          ? new Date(gaModalTrait as string)
          : new Date();
        const currentDate = new Date();
        if (
          (differenceInCalendarDays(currentDate, traitOptInDate) >
            DELAY_IN_DAYS ||
            differenceInCalendarDays(currentDate, gaModalTraitDate) >
              DELAY_IN_DAYS) &&
          source === 'boardScreen'
        ) {
          setIsEligible(true);
        } else {
          setIsEligible(false);
        }
      } finally {
        isLoadingTrait.current = false;
      }
    };
    // if the user is not in personal productivity, this eligibility check passes
    if (!isInPersonalProductivity && !loadingProductivityGate) {
      setIsEligible(true);
      return;
    }
    if (!isLoadingTrait.current) {
      getTrait();
    }
  }, [
    aaId,
    isInPersonalProductivity,
    loadingProductivityGate,
    source,
    isOptimizedUserTraitsEnabled,
    isCheckGaModalTrait,
  ]);

  useEffect(() => {
    if (
      !isOptimizedUserTraitsEnabled ||
      loadingOptInTrait ||
      loadingGaModalTrait
    ) {
      return;
    }

    // if the user is not in personal productivity, this eligibility check passes
    if (!isInPersonalProductivity && !loadingProductivityGate) {
      setIsEligible(true);
      return;
    }

    if (!aaId || !(optInTraitValue || gaModalTraitValue)) {
      setIsEligible(false);
      return;
    }
    const gaModalDate = isCheckGaModalTrait
      ? new Date(gaModalTraitValue as string)
      : new Date();

    const traitOptInDate = new Date(optInTraitValue as string);

    const currentDate = new Date();
    if (
      (differenceInCalendarDays(currentDate, traitOptInDate) > DELAY_IN_DAYS ||
        differenceInCalendarDays(currentDate, gaModalDate) > DELAY_IN_DAYS) &&
      source === 'boardScreen'
    ) {
      setIsEligible(true);
    } else {
      setIsEligible(false);
    }
  }, [
    isOptimizedUserTraitsEnabled,
    aaId,
    optInTraitValue,
    source,
    isInPersonalProductivity,
    loadingProductivityGate,
    loadingOptInTrait,
    loadingGaModalTrait,
    gaModalTraitValue,
    isCheckGaModalTrait,
  ]);

  return { isEligible };
};
